import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Alert, Button, Card, Col, Modal, Row, Select, Space, Statistic, Table, Tag, Typography, message,
} from "antd";
import { ClearOutlined, EyeOutlined, ThunderboltOutlined, WarningFilled } from "@ant-design/icons";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";
import { rightsService } from "../../../services/rightsService";

const { Title, Text } = Typography;
const PRIMARY = "#242166";
const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

/**
 * Épurage des attestations d'une compagnie : préserve les numéros consommés (gardés chez l'agence)
 * et libère le reste, regroupé en nouveaux lots par plages contiguës (suites). Aperçu (simulation)
 * obligatoire avant exécution. Exécution sous habilitation HME001.
 */
const EpurationAttestations = () => {
    const [compagnies, setCompagnies] = useState([]);
    const [compagnie, setCompagnie] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [executing, setExecuting] = useState(false);
    const canExecute = rightsService("HME001");
    const [searchParams] = useSearchParams();

    useEffect(() => {
        axiosInstance.get("/api/organisme/assurance")
            .then((res) => setCompagnies(res.data || []))
            .catch(() => message.error("Erreur lors du chargement des compagnies."));
    }, []);

    // Pré-sélection + aperçu auto quand on arrive depuis la page des lots d'une compagnie (?compagnie=CODE).
    useEffect(() => {
        const c = searchParams.get("compagnie");
        if (c) { setCompagnie(c); simulerFor(c); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const simulerFor = (code) => {
        if (!code) { message.warning("Sélectionnez une compagnie."); return; }
        setLoading(true);
        setPreview(null);
        axiosInstance.get(`/api/lot-attestation/epuration/simuler/${code}`)
            .then((res) => {
                setPreview({ ...res.data, execute: false });
                if (!res.data?.nbDispatchesModifies && !res.data?.nbDispatchesAnnules && !res.data?.nbNouveauxLots) {
                    message.info("Rien à épurer pour cette compagnie.");
                }
            })
            .catch(() => message.error("Erreur lors de la simulation."))
            .finally(() => setLoading(false));
    };
    const simuler = () => simulerFor(compagnie);

    const executer = () => {
        Modal.confirm({
            title: "Confirmer l'épurage",
            icon: <WarningFilled style={{ color: "#d4380d" }} />,
            content: (
                <div>
                    <p>Cette opération va <b>modifier les dispatches</b> (garder les numéros consommés), <b>libérer</b> les
                        numéros disponibles et <b>créer de nouveaux lots</b> par plages contiguës.</p>
                    <p style={{ color: "#888" }}>Action sensible et historisée. Confirmer ?</p>
                </div>
            ),
            okText: "Exécuter l'épurage",
            okButtonProps: { danger: true },
            cancelText: "Annuler",
            onOk() {
                setExecuting(true);
                return axiosInstance.post(`/api/lot-attestation/epuration/executer/${compagnie}`)
                    .then((res) => { setPreview({ ...res.data, execute: true }); message.success("Épurage exécuté avec succès."); })
                    .catch((e) => message.error(e?.response?.data?.message || "Erreur lors de l'épurage."))
                    .finally(() => setExecuting(false));
            },
        });
    };

    const dispatchCols = [
        {
            title: "Lot dispatché", key: "lot",
            render: (_, r) => <Text code>{r.lotDebut != null ? `${fmt(r.lotDebut)} - ${fmt(r.lotFin)}` : (r.dispatchCodeId || "-")}</Text>,
        },
        { title: "Agence", dataIndex: "agence", key: "a" },
        { title: "Consommés (gardés)", dataIndex: "nbConsommes", key: "c", align: "right", render: (v) => <Tag color="green">{fmt(v)}</Tag> },
        { title: "Libérés", dataIndex: "nbLiberes", key: "l", align: "right", render: (v) => <Tag color="blue">{fmt(v)}</Tag> },
    ];
    const lotCols = [
        { title: "Type", dataIndex: "typeAttestation", key: "t", render: (v) => v || "-" },
        { title: "Série", dataIndex: "serie", key: "s", render: (v) => v || "-" },
        { title: "Début", dataIndex: "debut", key: "d", align: "right", render: fmt },
        { title: "Fin", dataIndex: "fin", key: "f", align: "right", render: fmt },
        { title: "Nombre", dataIndex: "nombre", key: "n", align: "right", render: (v) => <b>{fmt(v)}</b> },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"PRODUCTION"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <Title level={3} style={{ color: PRIMARY }}><ClearOutlined /> Épurage des attestations</Title>
                            <Text type="secondary">
                                Réinitialise les dispatches d'une compagnie : garde les numéros consommés, libère le reste
                                regroupé en nouveaux lots (suites) prêts à re-dispatcher.
                            </Text>

                            <Card style={{ borderRadius: 12, marginTop: 16 }}>
                                <Space wrap>
                                    <Select
                                        showSearch optionFilterProp="label" style={{ width: 340 }}
                                        placeholder="Sélectionner la compagnie"
                                        value={compagnie} onChange={(v) => { setCompagnie(v); setPreview(null); }}
                                        options={compagnies.map((c) => ({ value: c.code, label: c.raisonSociale }))}
                                    />
                                    <Button type="primary" icon={<EyeOutlined />} loading={loading} onClick={simuler}
                                        style={{ background: PRIMARY }}>
                                        Simuler (aperçu)
                                    </Button>
                                    <Button danger icon={<ThunderboltOutlined />} loading={executing}
                                        disabled={!preview || preview.execute || !canExecute}
                                        onClick={executer}>
                                        Exécuter l'épurage
                                    </Button>
                                </Space>
                                {!canExecute && (
                                    <Alert style={{ marginTop: 12 }} type="info" showIcon
                                        message="Exécution réservée à l'habilitation HME001 (l'aperçu reste disponible)." />
                                )}
                            </Card>

                            {preview && (
                                <>
                                    {preview.execute && (
                                        <Alert style={{ marginTop: 16 }} type="success" showIcon
                                            message="Épurage exécuté." description="Les changements ci-dessous ont été appliqués." />
                                    )}
                                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                        <Col xs={12} md={6}><Card><Statistic title="Consommés gardés" value={preview.totalConsommesGardes} groupSeparator=" " /></Card></Col>
                                        <Col xs={12} md={6}><Card><Statistic title="Numéros libérés" value={preview.totalLiberes} valueStyle={{ color: "#1677ff" }} groupSeparator=" " /></Card></Col>
                                        <Col xs={12} md={6}><Card><Statistic title="Dispatches modifiés" value={preview.nbDispatchesModifies} /></Card></Col>
                                        <Col xs={12} md={6}><Card><Statistic title="Dispatches annulés" value={preview.nbDispatchesAnnules} /></Card></Col>
                                    </Row>

                                    <Card title={`Nouveaux lots générés (suites) — ${preview.nbNouveauxLots}`}
                                        style={{ borderRadius: 12, marginTop: 16 }}>
                                        <Table rowKey={(r, i) => `${r.debut}-${r.fin}-${i}`} size="small"
                                            columns={lotCols} dataSource={preview.nouveauxLots}
                                            pagination={{ pageSize: 10, showSizeChanger: true }} />
                                    </Card>

                                    <Card title={`Dispatches modifiés (consommés gardés) — ${preview.nbDispatchesModifies}`}
                                        style={{ borderRadius: 12, marginTop: 16 }}>
                                        <Table rowKey="dispatchCodeId" size="small"
                                            columns={dispatchCols} dataSource={preview.dispatchesModifies}
                                            pagination={{ pageSize: 8 }} />
                                    </Card>

                                    {preview.dispatchesAnnules?.length > 0 && (
                                        <Card title={`Dispatches annulés (0 consommé) — ${preview.nbDispatchesAnnules}`}
                                            style={{ borderRadius: 12, marginTop: 16 }}>
                                            <Table rowKey="dispatchCodeId" size="small"
                                                columns={dispatchCols.filter((c) => c.key !== "c")} dataSource={preview.dispatchesAnnules}
                                                pagination={{ pageSize: 8 }} />
                                        </Card>
                                    )}
                                </>
                            )}
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EpurationAttestations;
